import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, Users, Mail, ChevronDown, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HistoryView = ({ templates }) => {
  const [logs, setLogs] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let active = true;
    const load = () => axios.get(`${API}/campaigns`).then((r) => active && setLogs(r.data.campaigns)).catch(() => {});
    load();
    const t = setInterval(load, 3000);
    return () => { active = false; clearInterval(t); };
  }, []);

  const nameOf = (id) => templates.find((t) => t.id === id)?.name || id;

  return (
    <div data-testid="history-view" className="flex-1 overflow-auto p-8">
      <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-accent mb-1">Send history</p>
      <h2 className="font-serif-display text-3xl mb-2">Everything you've <em className="text-accent">sent.</em></h2>
      <p className="text-xs text-muted-foreground mb-6">Rows with failures can be clicked to see which addresses failed and why.</p>
      {!logs ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : logs.length === 0 ? (
        <p data-testid="history-empty" className="text-sm text-muted-foreground">No sends yet — send a template or start a bulk campaign.</p>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/60 text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                <th className="px-4 py-3 font-bold">Date</th>
                <th className="px-4 py-3 font-bold">Type</th>
                <th className="px-4 py-3 font-bold">Template</th>
                <th className="px-4 py-3 font-bold">From</th>
                <th className="px-4 py-3 font-bold text-right">Recipients</th>
                <th className="px-4 py-3 font-bold text-right">Sent</th>
                <th className="px-4 py-3 font-bold text-right">Failed</th>
                <th className="px-4 py-3 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => {
                const hasFailures = (l.failures?.length || 0) > 0;
                const isOpen = expanded === l.id;
                return [
                  <tr
                    key={l.id || l.timestamp}
                    data-testid={`history-row-${l.id}`}
                    onClick={() => hasFailures && setExpanded(isOpen ? null : l.id)}
                    className={`border-t border-border ${hasFailures ? "cursor-pointer hover:bg-secondary/40" : ""}`}
                  >
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {new Date(l.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                        l.type === "bulk" ? "bg-accent/15 text-accent" : "bg-secondary text-muted-foreground"
                      }`}>
                        {l.type === "bulk" ? <Users size={11} /> : <Mail size={11} />}
                        {l.type || "single"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold">{nameOf(l.template_id)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{l.sender?.from_email}</td>
                    <td className="px-4 py-3 text-right">{l.total ?? l.sent + l.failed}</td>
                    <td className="px-4 py-3 text-right font-semibold">{l.sent}</td>
                    <td className="px-4 py-3 text-right">
                      {hasFailures ? (
                        <span data-testid={`history-failed-${l.id}`} className="inline-flex items-center gap-1 text-destructive font-semibold">
                          {l.failed} <ChevronDown size={12} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </span>
                      ) : (
                        <span className={l.failed ? "text-destructive font-semibold" : "text-muted-foreground"}>{l.failed}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {l.status === "sending" ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent">
                          <Loader2 size={12} className="animate-spin" /> sending
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                          <CheckCircle2 size={12} /> completed
                        </span>
                      )}
                    </td>
                  </tr>,
                  isOpen && hasFailures && (
                    <tr key={`${l.id}-failures`} data-testid={`history-failures-${l.id}`} className="border-t border-border bg-destructive/5">
                      <td colSpan={8} className="px-6 py-4">
                        <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-destructive mb-2 flex items-center gap-1.5">
                          <AlertCircle size={12} /> Failed recipients
                        </p>
                        <div className="space-y-1">
                          {l.failures.map((f, i) => (
                            <p key={i} className="text-xs">
                              <span className="font-mono font-semibold">{f.email}</span>
                              <span className="text-muted-foreground"> — {f.error}</span>
                            </p>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
